// Решение по предложению из выгрузки мессенджера (сценарий 112).
//
// Это единственный путь из предложения в запись чёрного списка, и он ручной по требованию
// тз 5.8.2: классификацию делает заказчик, Система её не принимает. Автоматического вызова у
// этого действия нет и не будет.
//
// ## Три исхода, и они не равнозначны
//
// «В чёрный список» заводит настоящую запись — человека перестанут нанимать по всей группе
// юрлиц, поэтому здесь появляются причина и правовое основание. «Отметка о нарушении»
// оставляет след, но найму не мешает. «Отбросить» говорит, что модель ошиблась.
//
// Выбор сделан справочником, а не тремя кнопками в ряд: панель шириной 384 точки, а у кнопок
// стоит запрет переноса — «В чёрный список | Отметка о нарушении | Отбросить» налезли бы друг
// на друга (`.claude/rules/ui-views.md`, замер 25.08.2026 по выбору вида тарифа).

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';
import { TextField } from '@/shared/ui/text-field';

import type { BlacklistReason, DraftDecisionIn, DraftVerdict } from '../api/blacklist';
import { BLACKLIST_REASONS } from '../api/blacklist';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { BLACKLIST_REASON_TITLES, DRAFT_VERDICT_TITLES } from '../dictionaries';
import { useDecideDraftMutation, useDraftsQuery } from '../queries';

/** Решение по предложению: право рекрутёра и администратора. */
const EDIT_PERMISSION = 'hiring.blacklist.edit';

/** Столько знаков правового основания требует сервер. */
const MIN_BASIS = 3;

/** Исходы решения. «Ждёт решения» из списка убрано: это не исход, а его отсутствие. */
const ИСХОДЫ: readonly Exclude<DraftVerdict, 'pending'>[] = [
  'to_blacklist',
  'note_only',
  'discarded',
];

/** Что означает каждый исход. Название без этого не отвечает на вопрос «а что изменится». */
const ПОСЛЕДСТВИЯ: Readonly<Record<Exclude<DraftVerdict, 'pending'>, string>> = {
  to_blacklist:
    'Заведётся настоящая запись: этого человека перестанут нанимать по всей группе юрлиц, и сверка будет находить его при каждом отклике.',
  note_only: 'Останется след о нарушении. Найму он не мешает: сверка такого человека пропустит.',
  discarded: 'Предложение уйдёт из очереди как ошибка разбора. Никаких следов о человеке.',
};

type FieldName = 'verdict' | 'reason' | 'reason_note' | 'legal_basis';

export function DraftDecisionPanel({ draftId, onClose }: { draftId: string; onClose: () => void }) {
  const allowed = useCan(EDIT_PERMISSION);
  // Отдельного маршрута «одно предложение» в контракте нет. Берём его из списка без отбора:
  // так предложение находится и тогда, когда панель открыли по прямой ссылке при другом
  // отборе в адресе.
  const drafts = useDraftsQuery({});
  const draft = drafts.data?.items.find((строка) => строка.id === draftId);
  const mutation = useDecideDraftMutation();

  const [verdict, setVerdict] = useState(NO_VALUE);
  const [reason, setReason] = useState(NO_VALUE);
  const [reasonNote, setReasonNote] = useState('');
  const [legalBasis, setLegalBasis] = useState('');
  const [region, setRegion] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);

  const вСписок = verdict === 'to_blacklist';
  const предложенная = предложеннаяПричина(draft?.suggested_reason);
  // Пусто в поле причины означает «взять предложенную моделью» — так это и подписано. Своего
  // значения по умолчанию не подставляем: сервер уже умеет этот случай, а подставленная молча
  // причина превратила бы догадку модели в решение человека.
  const причинаДляОтправки = reason === NO_VALUE ? null : (reason as BlacklistReason);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Принимать решения по предложениям может рекрутёр или администратор. Попросите открыть это право — заполненное никуда не денется.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Решение уже отправлено, ждём ответ сервера. Второй раз не отправляем.');
      return;
    }
    if (draft !== undefined && draft.verdict !== 'pending') {
      setGateNote(
        `По этому предложению решение уже принято: «${draft.verdict_title}». Решения принимаются один раз.`,
      );
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (verdict === NO_VALUE) {
      next.verdict = 'Выберите решение: без него непонятно, что делать с предложением.';
    }
    if (вСписок) {
      const итоговая = причинаДляОтправки ?? предложенная;
      if (итоговая === null) {
        next.reason =
          'Модель причину не предложила, поэтому её надо выбрать: запись без причины не объясняет, за что человек в списке.';
      }
      if (итоговая === 'other' && reasonNote.trim() === '') {
        next.reason_note =
          'Причина «иное» без пояснения не говорит ничего ни рекрутёру, ни проверяющему. Напишите, что именно произошло.';
      }
      if (legalBasis.trim().length < MIN_BASIS) {
        next.legal_basis =
          'Правовое основание указывает заказчик, система его не придумывает: назовите документ или решение, на котором встанет запись.';
      }
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: DraftDecisionIn = вСписок
      ? {
          verdict: 'to_blacklist',
          reason: причинаДляОтправки,
          reason_note: reasonNote.trim() === '' ? null : reasonNote.trim(),
          legal_basis: legalBasis.trim(),
          region: region.trim() === '' ? null : region.trim(),
        }
      : { verdict: verdict as 'note_only' | 'discarded' };
    mutation.mutate({ draftId, body }, { onSuccess: () => onClose() });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {drafts.data === undefined && drafts.isPending ? (
        // Скелет только пока запрос идёт: условие «данных нет» держало бы его вечно, а вечный
        // скелет читается как «сейчас догрузится» и не догружается никогда.
        <Skeleton className='h-4 w-56' />
      ) : draft === undefined ? (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          Показать, что предложила модель, не удалось. Форма работает: решение сохранится, но
          причину и основание придётся заполнить самому.
        </Hint.Root>
      ) : (
        <p className='text-paragraph-xs text-text-sub-600'>
          {(draft.full_name ?? '').trim() === '' ? 'Имя не распознано' : draft.full_name}
          {(draft.phone_normalized ?? '').trim() === '' ? '' : ` · ${draft.phone_normalized}`}
        </p>
      )}

      {!allowed && (
        <NoPermissionNote
          что='принимать решения по предложениям'
          кто='Это делают рекрутёр и администратор системы.'
        />
      )}

      {draft !== undefined && draft.verdict !== 'pending' && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          По этому предложению уже принято решение «{draft.verdict_title}». Решения принимаются один
          раз, и новое сервер не примет.
        </Hint.Root>
      )}

      <SelectField
        id='draft-decision-verdict'
        label='Решение'
        required
        value={verdict}
        onChange={setVerdict}
        options={ИСХОДЫ.map((code) => ({ value: code, label: DRAFT_VERDICT_TITLES[code] }))}
        placeholder='Выберите решение'
        error={errors.verdict}
        // Последствие показывается только у выбранного исхода: три пояснения сразу — это
        // абзац над формой, который никто не читает.
        hint={
          verdict === NO_VALUE
            ? 'Автоматического пути из предложения в чёрный список нет: решение принимает человек.'
            : ПОСЛЕДСТВИЯ[verdict as Exclude<DraftVerdict, 'pending'>]
        }
      />

      {вСписок && (
        <>
          <SelectField
            id='draft-decision-reason'
            label='Причина'
            value={reason}
            onChange={setReason}
            options={BLACKLIST_REASONS.map((code) => ({
              value: code,
              label: BLACKLIST_REASON_TITLES[code],
            }))}
            noneLabel={
              предложенная === null
                ? 'Не выбрана'
                : `Оставить предложенную моделью: ${BLACKLIST_REASON_TITLES[предложенная]}`
            }
            placeholder='Выберите причину'
            error={errors.reason}
            hint={
              предложенная === null
                ? 'Модель причину не предложила — выберите её сами.'
                : 'Причину предложила модель. Оставьте её или смените: последнее слово за вами.'
            }
          />

          <TextArea
            id='draft-decision-reason-note'
            label='Уточнение причины'
            required={(причинаДляОтправки ?? предложенная) === 'other'}
            rows={2}
            value={reasonNote}
            onChange={setReasonNote}
            placeholder='Что именно произошло, коротко'
            error={errors.reason_note}
            hint='Не заполните — в запись уйдёт текст исходного сообщения из переписки.'
          />

          <TextArea
            id='draft-decision-legal-basis'
            label='Правовое основание'
            required
            rows={2}
            value={legalBasis}
            onChange={setLegalBasis}
            placeholder='Например: акт от 12.08.2026, объяснительная, приказ № 14'
            error={errors.legal_basis}
            hint='Указывает заказчик: система оснований не придумывает.'
          />

          <TextField
            id='draft-decision-region'
            label='Регион'
            value={region}
            onChange={setRegion}
            placeholder='Ханты-Мансийский АО'
            hint='Помогает отличить однофамильцев при сверке. Список при этом общий по всей группе юрлиц.'
          />
        </>
      )}

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && <GateNote>{gateNote}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Сохраняем…' : 'Принять решение'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}

/** Причина, предложенная моделью, если она из справочника. Чужой код показывать нечем. */
function предложеннаяПричина(код: string | null | undefined): BlacklistReason | null {
  const текст = (код ?? '').trim();
  return (BLACKLIST_REASONS as readonly string[]).includes(текст)
    ? (текст as BlacklistReason)
    : null;
}
